import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Calendar, Search, Users, ChevronDown, ChevronRight, CheckCircle, XCircle, Clock as ClockIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return `${dt.getDate()} ${dt.toLocaleString('en-IN', { month: 'short' })}`;
};

const fmtDayShort = (d) => {
  if (!d) return '';
  return DAY_NAMES[new Date(d + 'T00:00:00').getDay()];
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-700 border-red-200',
  };
  const icons = {
    approved: <CheckCircle className="h-3 w-3 mr-1" />,
    rejected: <XCircle className="h-3 w-3 mr-1" />,
    pending: <ClockIcon className="h-3 w-3 mr-1" />,
  };
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border ${map[status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>
      {icons[status]}{(status || '').toUpperCase()}
    </span>
  );
};

const RosterCard = ({ roster, outletName, defaultExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const entries = roster.entries || [];

  // Pivot entries into grid: rows=employees, cols=dates
  const employees = useMemo(() => {
    const empMap = {};
    entries.forEach(e => {
      if (!empMap[e.employee_name]) empMap[e.employee_name] = {};
      empMap[e.employee_name][e.date] = e;
    });
    return Object.entries(empMap).sort(([a], [b]) => a.localeCompare(b));
  }, [entries]);

  const dates = useMemo(() => {
    return [...new Set(entries.map(e => e.date))].sort();
  }, [entries]);

  const approvedBy = (roster.approvals || []).map(a => a.user_name).join(', ');

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`roster-card-${roster.id}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left"
        data-testid={`roster-toggle-${roster.id}`}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900 text-sm">{outletName}</span>
            <StatusBadge status={roster.status} />
            <span className="text-xs text-slate-400">
              {dates.length > 0 ? `${fmtDate(dates[0])} — ${fmtDate(dates[dates.length - 1])}` : 'No dates'}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-[11px] text-slate-500">
            <span>{employees.length} employees</span>
            <span>{entries.length} shifts</span>
            <span>by {roster.created_by_name}</span>
            {approvedBy && <span className="text-emerald-600">Approved: {approvedBy}</span>}
          </div>
        </div>
      </button>

      {/* Schedule grid */}
      {expanded && dates.length > 0 && (
        <div className="border-t border-slate-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="px-3 py-2 text-left font-semibold sticky left-0 bg-slate-800 z-10 min-w-[140px]">Employee</th>
                {dates.map(d => (
                  <th key={d} className="px-1.5 py-2 text-center font-medium min-w-[90px]">
                    <div>{fmtDayShort(d)}</div>
                    <div className="text-[10px] text-slate-300 font-normal">{fmtDate(d)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {employees.map(([empName, schedule]) => (
                <tr key={empName} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-semibold text-slate-900 whitespace-nowrap sticky left-0 bg-inherit z-10">{empName}</td>
                  {dates.map(d => {
                    const entry = schedule[d];
                    if (!entry) return <td key={d} className="px-1.5 py-2 text-center text-slate-300">—</td>;
                    const isOff = entry.shift_type === 'OFF' || entry.shift_type === 'WO';
                    return (
                      <td key={d} className="px-1.5 py-2 text-center">
                        {isOff ? (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">OFF</span>
                        ) : (
                          <div>
                            <div className="text-[10px] font-semibold text-slate-700">{entry.shift_template_name || entry.shift_type}</div>
                            <div className="text-[9px] text-slate-400 mt-0.5">{entry.shift_time}</div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {expanded && dates.length === 0 && (
        <div className="border-t border-slate-200 p-6 text-center text-slate-400 text-sm">No shift entries in this roster.</div>
      )}
    </div>
  );
};

const ShiftProRosters = () => {
  const [rosters, setRosters] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [search, setSearch] = useState('');

  useEffect(() => { fetchOutlets(); }, []);
  useEffect(() => { fetchRosters(); }, []);

  const fetchOutlets = async () => {
    try { const res = await api.get('/shiftpro/outlets'); setOutlets(res.data || []); } catch { /* ignore */ }
  };

  const fetchRosters = async () => {
    setLoading(true);
    try {
      const res = await api.get('/shiftpro/rosters');
      setRosters(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to fetch rosters');
      setRosters([]);
    } finally { setLoading(false); }
  };

  const outletMap = useMemo(() => {
    const m = {};
    outlets.forEach(o => { m[o.id] = o.name; });
    return m;
  }, [outlets]);

  // Filter rosters
  const filtered = useMemo(() => {
    const [yr, mo] = selectedMonth ? selectedMonth.split('-').map(Number) : [0, 0];
    return rosters.filter(r => {
      // Only approved
      if (r.status !== 'approved') return false;
      // Month filter
      if (yr && mo && (r.year !== yr || r.month !== mo)) return false;
      // Outlet filter
      if (selectedOutlet && r.outlet_id !== selectedOutlet) return false;
      // Search by employee name in entries
      if (search) {
        const s = search.toLowerCase();
        const hasMatch = (r.entries || []).some(e => (e.employee_name || '').toLowerCase().includes(s));
        if (!hasMatch) return false;
      }
      return true;
    });
  }, [rosters, selectedMonth, selectedOutlet, search]);

  return (
    <div className="space-y-5" data-testid="shiftpro-rosters-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">ShiftPro Rosters</h1>
          <p className="mt-1 text-base text-slate-600">Approved shift rosters from ShiftPro</p>
        </div>
        <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-1.5">
          <Users className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-semibold text-violet-700">{filtered.length} Rosters</span>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div>
          <Label>Month</Label>
          <div className="flex items-center gap-2 mt-1">
            <Calendar className="h-4 w-4 text-slate-500" />
            <Input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="w-44" data-testid="roster-month" />
          </div>
        </div>
        <div className="min-w-[180px]">
          <Label>Outlet</Label>
          <select value={selectedOutlet} onChange={e => setSelectedOutlet(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1" data-testid="roster-outlet-filter">
            <option value="">All Outlets</option>
            {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="min-w-[200px]">
          <Label>Search Employee</Label>
          <div className="relative mt-1">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Employee name..." className="pl-9" data-testid="roster-search" />
          </div>
        </div>
      </div>

      {/* Rosters */}
      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <p className="text-slate-500">No approved rosters found for this period.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="roster-list">
          {filtered.map((roster, idx) => (
            <RosterCard
              key={roster.id}
              roster={roster}
              outletName={outletMap[roster.outlet_id] || 'Unknown Outlet'}
              defaultExpanded={idx === 0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ShiftProRosters;
